import { ClerkProvider } from "@clerk/nextjs";
import { HeroUIProvider } from "@heroui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import classNames from "classnames";
import HandleAnalytic from "components/HandleAnalytic";
import HandleScreenSizes from "components/HandleScreenSizes";
import InitializeHooks from "components/InitializeHooks";
import IntercomButton from "components/IntercomButton";
import { QueryLanguageModal } from "components/QueryLanguageModal";
import { RightSidebarProvider } from "components/RightSidebar";
import {
  PremiumFeatureModal,
  SubscriptionProvider,
} from "features/Subscription/components";
import { StudentDiscountModal } from "features/Subscription/components/StudentDiscountModal";
import ClaimStudentDiscountModal from "features/Subscription/components/ClaimStudentDiscountModal";
import TopBar from "components/TopBar";
import { PARTNERSTACK_PUBLIC_KEY } from "constants/config";
import { SaveBookmarkModal } from "features/Bookmarks";
import { StatsigWrapper } from "features/Statsig";
import FilterDrawer from "features/FilterDrawer";
import { InstitutionSelectionModalWrapper } from "features/libraries";
import SearchInput from "features/SearchInput";
import Sidebar, { SidebarOverlay } from "features/Sidebar";
import { initChurnkey } from "helpers/churnkey";
import "helpers/polyfills";
import { isIntercomEnabled } from "helpers/services/intercom";
import { IntercomProvider } from "helpers/services/intercom/IntercomProvider";
import * as metaPixel from "helpers/services/metaPixel";
import * as mixpanel from "helpers/services/mixpanel";
import { useFingerprint } from "hooks/useFingerprint";
import useLabels from "hooks/useLabels";
import type { AppContext, AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { store } from "store";
import "styles/clerk.css";
import "styles/globals.css";
import "../../public/fontello/css/fontello.css";
import { initializeTheme } from "design_system/utils/theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type MyAppProps = AppProps & {};

/**
 * @description Wrapper component to all of the pages.
 */
const MyApp = ({ Component, pageProps }: MyAppProps) => {
  const [pageTitle] = useLabels("page-title");
  const { hideSearchBar } = pageProps;
  const rightSidebarPortalRef = useRef<HTMLDivElement>(null);

  // Initialize services
  useEffect(() => {
    mixpanel.initialize();
    metaPixel.initialize();
    initChurnkey();
    initializeTheme();
  }, []);

  // Initialize fingerprinting
  useFingerprint();

  return (
    <IntercomProvider enabled={isIntercomEnabled}>
      <SubscriptionProvider>
        <ClerkProvider
          {...pageProps}
          appearance={{
            elements: {
              card: "!shadow-none",
              formButtonPrimary:
                "!rounded-xl !bg-accent-bg-base !text-accent-fg-on-accent hover:!bg-accent-bg-emphasis !border-white/8 !shadow-none !h-10 !base-md",
              buttonArrowIcon: "!hidden",
              socialButtonsBlockButtonText: "!base-md",
            },
            variables: {
              fontSize: "1rem",
            },
            layout: {
              logoImageUrl: "/icons/logo-full.svg",
              socialButtonsPlacement: "bottom",
            },
          }}
        >
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <HeroUIProvider>
                <Head>
                  <title>{pageTitle}</title>
                  <meta
                    property="og:title"
                    content="Consensus"
                    key="og:title"
                  />
                  <meta name="robots" content="all" />
                  <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                  />
                  <link rel="icon" type="image/png" href="/favicon.png" />
                </Head>
                <Script
                  id="gtm-script"
                  strategy="afterInteractive"
                >{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                      })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');`}</Script>
                <Script src="https://js.stripe.com/v3" async></Script>
                <Script
                  id="partnerstack-script"
                  strategy="afterInteractive"
                >{`(function() {var gs = document.createElement('script');gs.src = 'https://get.consensus.app/pr/js';gs.type = 'text/javascript';gs.async = 'true';gs.onload = gs.onreadystatechange = function() {var rs = this.readyState;if (rs && rs != 'complete' && rs != 'loaded') return;try {growsumo._initialize('${PARTNERSTACK_PUBLIC_KEY}'); if (typeof(growsumoInit) === 'function') {growsumoInit();}} catch (e) {}};var s = document.getElementsByTagName('script')[0];s.parentNode.insertBefore(gs, s);})();`}</Script>
                <StatsigWrapper>
                  <div className={pageProps?.pathname?.replaceAll("/", "")}>
                    <HandleAnalytic />
                    <HandleScreenSizes />
                    <InitializeHooks />
                    <div
                      className={classNames(
                        "grid min-h-screen transition-[grid-template-columns] duration-300",
                        pageProps.hideSidebar
                          ? "grid-cols-[0_1fr_auto]"
                          : "grid-cols-[var(--sidebar-grid-width)_1fr_auto]"
                      )}
                    >
                      {!pageProps.hideSidebar ? (
                        <aside className="h-dvh overflow-y-auto flex flex-col bg-bg-base z-50 sticky top-0 transition-[width] duration-300 w-(--sidebar-width)">
                          <Sidebar />
                        </aside>
                      ) : null}

                      {/* Main Content */}
                      <main
                        className={classNames(
                          "min-w-0 col-start-2",
                          !pageProps.hideBackgroundGradient &&
                            "bg-gradient-stretch"
                        )}
                      >
                        {!pageProps.hideSidebar ? <SidebarOverlay /> : null}
                        {!pageProps.hideTopBar ? <TopBar /> : null}
                        {!hideSearchBar ? (
                          <div className="sticky top-0 z-20 w-full">
                            <div
                              className={classNames(
                                `max-w-page mx-auto`,
                                pageProps.pageContainerClassname
                              )}
                            >
                              <div className="px-4 pt-4 pb-4 sm:px-6 md:px-0 md:py-6">
                                <SearchInput />
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <div className="flex-1">
                          <div
                            className={classNames(
                              !pageProps.fullWidthPage && `max-w-page mx-auto`,
                              pageProps.pageContainerClassname
                            )}
                          >
                            <RightSidebarProvider
                              portalRef={rightSidebarPortalRef}
                            >
                              <Component {...pageProps} />
                            </RightSidebarProvider>
                          </div>

                          {/* Default Intercom button - pages can opt out with hideDefaultIntercomButton prop */}
                          {!pageProps.hideDefaultIntercomButton && (
                            <IntercomButton />
                          )}

                          {<PremiumFeatureModal />}
                          {<QueryLanguageModal />}
                          <StudentDiscountModal />
                          <ClaimStudentDiscountModal />
                          <SaveBookmarkModal />
                          <InstitutionSelectionModalWrapper />
                        </div>
                      </main>
                      {/* Right Sidebar - positioned absolutely within main */}
                      <aside
                        ref={rightSidebarPortalRef}
                        className="sticky top-0 h-dvh z-50"
                      >
                        {/* Portal content will be rendered here */}
                      </aside>
                    </div>
                    <FilterDrawer />
                    <ToastContainer
                      position="top-center"
                      closeButton={false}
                      hideProgressBar
                      theme="dark"
                      icon={false}
                      autoClose={3000}
                      toastClassName={(context) =>
                        classNames(
                          "!w-fit !min-w-0 p-1 pl-3 pr-3 mt-2 rounded-md overflow-hidden cursor-pointer text-white text-sm mx-auto",
                          {
                            "bg-chart-red-base": context?.type === "error",
                            "bg-positive-bg-base": context?.type !== "error",
                          }
                        )
                      }
                      bodyClassName={() =>
                        "flex justify-center items-center text-sm py-1 px-1 w-fit"
                      }
                    />
                  </div>
                </StatsigWrapper>
              </HeroUIProvider>
            </Provider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ClerkProvider>
      </SubscriptionProvider>
    </IntercomProvider>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: AppContext) => {
  let pageProps: any = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  pageProps.pathname = ctx.pathname;

  // Special handling for 404 error pages
  if (ctx.pathname === "/404") {
    pageProps.hideBookmarks = true;
    pageProps.hideSearchBar = true;
    pageProps.fullWidthPage = true;
    pageProps.hideTopBar = true;
  }

  return { pageProps };
};

export default MyApp;
